import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  Info,
  Landmark,
  Loader2,
  PartyPopper,
  QrCode,
  RefreshCw,
  ShieldCheck,
  Trash2,
  Wallet,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ONBOARDING_VERIFICATION_ROUTE } from "@/modules/verification/constants/verification.constants";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaChoiceCards } from "@/shared/components/forms/ColocaChoiceCards";
import { ColocaStepper } from "@/shared/components/navigation/ColocaStepper";
import { TwoFactorActionModals } from "@/shared/components/overlays/TwoFactorActionModals";
import {
  type UseBrebReceivingKey,
  useBrebKeyOptions,
  useBrebReceivingKey,
} from "@/shared/hooks/useBrebReceivingKey";
import { useTwoFactorAction } from "@/shared/hooks/useTwoFactorAction";
import type { Translations } from "@/shared/lang/interfaces/translationTypes";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import {
  brebKeyDeleteImpact,
  type BrebKeyDeleteOutcome,
  type BrebKeyAvailability,
  type BrebKeyFailureReason,
  type BrebKeyLastFailure,
  type BrebKeyOption,
  type BrebKeySource,
  type BrebReceivingKeyState,
  isBrebKeyCreateRefused,
  isBrebKeyDeleteBlockedByLiveQrs,
} from "@/shared/services/payments/brebReceivingKey.service";
import { colocaToast } from "@/shared/utils/toast";
import { isTwoFactorCodeError } from "@/shared/utils/twoFactorErrors";

import { BrebKeyList, BrebKeySetPrimaryAction } from "./BrebKeyList";
import { BrebKeyPanel } from "./BrebKeyPanel";
import { BrebKeyPlate } from "./BrebKeyPlate";
import { BREB_KEY_SOURCE_ICON } from "./brebKeySources";

type WizardStep = "intro" | "choose" | "confirm" | "creating" | "error";

const STEP_INDEX: Record<WizardStep, number> = { intro: 0, choose: 1, confirm: 2, creating: 2, error: 2 };
const KEY_PLACEHOLDER = "@coloca…";
const LAST_FAILURE_TEXT: Record<BrebKeyFailureReason, keyof Translations["payins"]> = {
  key_taken: "breb_key_last_failure_taken",
  key_rejected: "breb_key_last_failure_rejected",
  provider_rejected: "breb_key_last_failure_unfinished",
};
// Staff assign custom keys, so the member cannot pick another one: the way out is support.
const CUSTOM_LAST_FAILURE_TEXT: Record<BrebKeyFailureReason, keyof Translations["payins"]> = {
  key_taken: "breb_key_last_failure_custom_taken",
  key_rejected: "breb_key_last_failure_custom_rejected",
  provider_rejected: "breb_key_last_failure_custom_unfinished",
};
const SOURCE_LABEL: Record<BrebKeySource, keyof Translations["payins"]> = {
  DOCUMENT: "breb_key_option_document",
  PHONE: "breb_key_option_phone",
  GENERATED: "breb_key_option_generated",
};
const SOURCE_HINT: Record<BrebKeySource, keyof Translations["payins"]> = {
  DOCUMENT: "breb_key_option_document_hint",
  PHONE: "breb_key_option_phone_verified_hint",
  GENERATED: "breb_key_option_generated_hint",
};

const isTaken = (option: BrebKeyOption): boolean => option.availability === "TAKEN";
const noticeIdOf = (option: BrebKeyOption): string => `${option.source}:${option.key_value}`;

export interface BrebKeyFlowProps {
  organizationId?: string;
  /** The wizard's step, for a host that renders `BrebKeyWizardStepper` in its toolbar; null outside it. */
  onStepChange?: (step: number | null) => void;
  /** The key the ring opens on; the primary one otherwise. */
  initialKeyId?: string | null;
}

export function BrebKeyFlow({ organizationId, onStepChange, initialKeyId = null }: BrebKeyFlowProps) {
  const { t } = useTranslations("payins");
  const { can } = useAuth();
  const keyState = useBrebReceivingKey(organizationId);
  const { state, isLoading, isRefreshing, reload, create, notifyHolder } = keyState;
  const [justCreatedId, setJustCreatedId] = useState<string | null>(null);

  if (!state) {
    if (isLoading) {
      return (
        <div className="space-y-3" aria-busy="true">
          <ColocaSkeleton className="h-44 w-full rounded-3xl" />
          <ColocaSkeleton className="h-24 w-full" />
        </div>
      );
    }
    return (
      <ColocaAlert
        variant="destructive"
        icon={AlertCircle}
        text={t("breb_key_load_error")}
        actions={
          <ColocaButton size="sm" icon={RefreshCw} onClick={reload}>
            {t("breb_key_retry")}
          </ColocaButton>
        }
      />
    );
  }

  if (state.keys.length > 0) {
    return (
      <BrebKeyKeyring
        keyState={keyState}
        state={state}
        justCreatedId={justCreatedId}
        onCreated={setJustCreatedId}
        onStepChange={onStepChange}
        initialKeyId={initialKeyId}
      />
    );
  }

  if (!state.eligibility.enabled) {
    return <ColocaAlert variant="secondary" icon={Info} text={t("breb_key_not_enabled")} />;
  }

  if (!can("payments:write")) return <BrebKeyReadOnlyNotice />;

  if (!state.eligibility.verified) return <BrebKeyVerificationRequired />;

  if (!state.eligibility.rail_enabled) {
    return <ColocaAlert variant="warning" icon={AlertCircle} text={t("breb_key_rail_disabled")} />;
  }

  if (state.options.length === 0) {
    return <ColocaAlert variant="warning" icon={AlertCircle} text={t("breb_key_no_options")} />;
  }

  return (
    <div className="mx-auto w-full max-w-2xl space-y-4">
      {state.last_failure && <BrebKeyLastFailureNotice failure={state.last_failure} />}
      <BrebKeyCreateWizard
        options={state.options}
        isRefreshing={isRefreshing}
        onCreate={async (source) => {
          const created = await create(source);
          setJustCreatedId(created.id);
        }}
        onStale={reload}
        onNotifyHolder={notifyHolder}
        onStepChange={onStepChange}
      />
    </div>
  );
}

interface BrebKeyKeyringProps {
  keyState: UseBrebReceivingKey;
  state: BrebReceivingKeyState;
  justCreatedId: string | null;
  onCreated: (keyId: string) => void;
  onStepChange?: (step: number | null) => void;
  initialKeyId: string | null;
}

function BrebKeyKeyring({
  keyState,
  state,
  justCreatedId,
  onCreated,
  onStepChange,
  initialKeyId,
}: BrebKeyKeyringProps) {
  const { t } = useTranslations("payins");
  const { can } = useAuth();
  const { create, remove, setPrimary, notifyHolder, scopedOrgId } = keyState;
  const [selectedId, setSelectedId] = useState<string | null>(initialKeyId);
  const [adding, setAdding] = useState(false);
  const addOptions = useBrebKeyOptions(scopedOrgId, adding);

  const canWrite = can("payments:write");
  const { enabled, verified, rail_enabled } = state.eligibility;
  const canAdd = canWrite && enabled && verified && rail_enabled && state.addable_sources.length > 0;
  const selected = state.keys.find((key) => key.id === selectedId) ?? state.keys[0];
  const deleteImpact = brebKeyDeleteImpact(state.keys, selected.id);
  const hasPrimary = state.keys.some((key) => key.is_primary);

  if (adding) {
    const firstLoad = addOptions.isLoading && addOptions.options.length === 0;
    return (
      <div className="mx-auto w-full max-w-2xl space-y-4">
        {firstLoad && (
          <div className="space-y-3" aria-busy="true">
            <ColocaSkeleton className="h-10 w-2/3" />
            <ColocaSkeleton className="h-40 w-full" />
          </div>
        )}
        {!firstLoad && addOptions.hasError && (
          <ColocaAlert
            variant="destructive"
            icon={AlertCircle}
            text={t("breb_key_options_load_error")}
            actions={
              <ColocaButton size="sm" icon={RefreshCw} onClick={addOptions.reload}>
                {t("breb_key_retry")}
              </ColocaButton>
            }
          />
        )}
        {!firstLoad && !addOptions.hasError && addOptions.options.length === 0 && (
          <ColocaAlert variant="secondary" icon={Info} text={t("breb_key_add_none")} />
        )}
        {!firstLoad && !addOptions.hasError && addOptions.options.length > 0 && (
          <BrebKeyCreateWizard
            options={addOptions.options}
            initialStep="choose"
            isFirstKey={!hasPrimary}
            isRefreshing={addOptions.isLoading}
            onCreate={async (source) => {
              const created = await create(source);
              setAdding(false);
              setSelectedId(created.id);
              onCreated(created.id);
            }}
            onStale={addOptions.reload}
            onNotifyHolder={notifyHolder}
            onStepChange={onStepChange}
            onCancel={() => setAdding(false)}
          />
        )}
        {(firstLoad || addOptions.hasError || addOptions.options.length === 0) && (
          <div className="flex justify-start">
            <ColocaButton variant="ghost" icon={ChevronLeft} onClick={() => setAdding(false)}>
              {t("breb_key_back")}
            </ColocaButton>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {justCreatedId === selected.id && selected.status === "ACTIVE" && (
        <ColocaAlert
          variant="success"
          icon={PartyPopper}
          heading={t("breb_key_ready_title")}
          text={t("breb_key_ready_body")}
        />
      )}
      {state.last_failure && <BrebKeyLastFailureNotice failure={state.last_failure} />}
      {(state.keys.length > 1 || canAdd) && (
        <BrebKeyList
          keys={state.keys}
          selectedId={selected.id}
          onSelect={setSelectedId}
          onAdd={canAdd ? () => setAdding(true) : undefined}
        />
      )}
      <BrebKeyPanel receivingKey={selected} organizationId={scopedOrgId} />
      {canWrite && (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
          {!selected.is_primary && selected.status === "ACTIVE" && (
            <BrebKeySetPrimaryAction
              key={`primary-${selected.id}`}
              keyValue={selected.key_value}
              onSetPrimary={() => setPrimary(selected.id)}
            />
          )}
          <BrebKeyDeleteAction
            key={`delete-${selected.id}`}
            keyValue={selected.key_value}
            canCreateAgain={enabled}
            outcome={deleteImpact.outcome}
            successorValue={deleteImpact.successorValue}
            onDelete={async (acceptLiveQrs, twoFactorCode) => {
              await remove(selected.id, acceptLiveQrs, twoFactorCode);
              setSelectedId(null);
            }}
          />
        </div>
      )}
    </div>
  );
}

export interface BrebKeyLastFailureNoticeProps {
  failure: BrebKeyLastFailure;
}

export function BrebKeyLastFailureNotice({ failure }: BrebKeyLastFailureNoticeProps) {
  const { t } = useTranslations("payins");
  const texts = failure.source === "CUSTOM" ? CUSTOM_LAST_FAILURE_TEXT : LAST_FAILURE_TEXT;
  return (
    <ColocaAlert
      variant="warning"
      icon={AlertCircle}
      heading={t("breb_key_last_failure_title")}
      text={t(texts[failure.reason] ?? texts.provider_rejected, { key: failure.key_value })}
    />
  );
}

export interface BrebKeyWizardStepperProps {
  currentStep: number;
}

export function BrebKeyWizardStepper({ currentStep }: BrebKeyWizardStepperProps) {
  const { t } = useTranslations("payins");
  return (
    <ColocaStepper
      steps={[
        { label: t("breb_key_wizard_step_intro") },
        { label: t("breb_key_wizard_step_choose") },
        { label: t("breb_key_wizard_step_confirm") },
      ]}
      currentStep={currentStep}
      aria-label={t("breb_key_label")}
    />
  );
}

export function BrebKeyReadOnlyNotice() {
  const { t } = useTranslations("payins");
  return <ColocaAlert variant="secondary" icon={Info} text={t("breb_key_read_only")} />;
}

export function BrebKeyVerificationRequired() {
  const { t } = useTranslations("payins");
  const navigate = useNavigate();
  return (
    <div className="rounded-2xl border border-border bg-card p-5 text-center">
      <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10">
        <ShieldCheck className="size-6 text-primary" aria-hidden="true" />
      </div>
      <h3 className="mt-3 text-base font-semibold text-foreground">{t("breb_key_verify_title")}</h3>
      <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">{t("breb_key_verify_body")}</p>
      <ColocaButton
        variant="primary"
        className="mt-4"
        onClick={() => navigate(ONBOARDING_VERIFICATION_ROUTE)}
      >
        {t("breb_key_verify_cta")}
      </ColocaButton>
    </div>
  );
}

/** `last`: no key is left; `primary`: `successorValue` takes over the QRs; `secondary`: the primary stays. */
export interface BrebKeyDeleteActionProps {
  keyValue: string;
  /** False outside the rollout: the dialog then does not promise that another key can be created. */
  canCreateAgain?: boolean;
  outcome?: BrebKeyDeleteOutcome;
  successorValue?: string;
  /** An icon button, for a row inside a list. */
  compact?: boolean;
  /** `twoFactorCode` comes only on the retry the backend asked for: users without 2FA are never prompted. */
  onDelete: (acceptLiveQrs: boolean, twoFactorCode?: string) => Promise<void>;
}

const deleteDescriptionKey = (
  outcome: BrebKeyDeleteOutcome,
  canCreateAgain: boolean,
): keyof Translations["payins"] => {
  if (outcome === "primary") return "breb_key_delete_body_primary";
  if (outcome === "secondary") return "breb_key_delete_body_secondary";
  return canCreateAgain ? "breb_key_delete_body" : "breb_key_delete_body_final";
};

// The dialog stays open on failure so the reason (e.g. QRs with an amount still payable) is read where it was asked.
// The backend answers the live-QR conflict before it asks for the code, so the code is typed once, last.
export function BrebKeyDeleteAction({
  keyValue,
  canCreateAgain = true,
  outcome = "last",
  successorValue,
  compact = false,
  onDelete,
}: BrebKeyDeleteActionProps) {
  const { t } = useTranslations("payins");
  const twoFactor = useTwoFactorAction<true>();
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [liveQrsWarning, setLiveQrsWarning] = useState<string | null>(null);

  const attempt = async (acceptLiveQrs: boolean): Promise<boolean> => {
    try {
      await onDelete(acceptLiveQrs);
      return true;
    } catch (err) {
      if (!isTwoFactorCodeError(err)) throw err;
      const done = await twoFactor.start(async (code) => {
        await onDelete(acceptLiveQrs, code);
        return true as const;
      });
      return done === true;
    }
  };

  const confirm = async () => {
    setDeleting(true);
    setError(null);
    try {
      if (!(await attempt(liveQrsWarning !== null))) return;
      setOpen(false);
      setLiveQrsWarning(null);
      colocaToast.success(t("breb_key_delete_done", { key: keyValue }));
    } catch (err) {
      if (liveQrsWarning === null && isBrebKeyDeleteBlockedByLiveQrs(err)) {
        setLiveQrsWarning(HttpApiService.getErrorMessage(err));
      } else {
        setError(HttpApiService.getErrorMessage(err));
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <ColocaAlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && deleting) return;
        setOpen(next);
        if (!next) {
          setError(null);
          setLiveQrsWarning(null);
        }
      }}
      trigger={
        compact ? (
          <ColocaIconButton
            size="sm"
            variant="ghost"
            icon={Trash2}
            aria-label={t("breb_key_delete_title", { key: keyValue })}
          />
        ) : (
          <ColocaButton size="sm" variant="destructive" icon={Trash2}>
            {t("breb_key_delete")}
          </ColocaButton>
        )
      }
      title={t("breb_key_delete_title", { key: keyValue })}
      description={t(deleteDescriptionKey(outcome, canCreateAgain), {
        key: keyValue,
        next: successorValue ?? "",
      })}
      confirmLabel={liveQrsWarning ? t("breb_key_delete_anyway") : t("breb_key_delete_confirm")}
      confirmVariant="destructive"
      confirmDisabled={deleting}
      cancelLabel={t("breb_key_delete_cancel")}
      onConfirm={() => void confirm()}
    >
      {liveQrsWarning && <ColocaAlert variant="warning" icon={AlertCircle} text={liveQrsWarning} />}
      {error && <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />}
      <TwoFactorActionModals
        controller={twoFactor}
        requiredDescription={t("breb_key_delete_two_factor_required")}
        challengeDescription={t("breb_key_delete_two_factor_description", { key: keyValue })}
      />
    </ColocaAlertDialog>
  );
}

export interface BrebKeyCreateWizardProps {
  options: BrebKeyOption[];
  /** `choose` skips the introduction, for an organization that already holds a key. */
  initialStep?: "intro" | "choose";
  /** False when the organization already has a primary key, which keeps minting its QRs. */
  isFirstKey?: boolean;
  /** Leaves the wizard from its first step. */
  onCancel?: () => void;
  /** The options are being read again; their badges say so meanwhile. */
  isRefreshing?: boolean;
  onCreate: (source: BrebKeySource) => Promise<void>;
  /** Re-reads the options: after an UNKNOWN, or after the chosen key was refused. */
  onStale: () => void;
  /** Asks to tell the Coloca organization holding a taken value; true once an email is on record. */
  onNotifyHolder?: (source: BrebKeySource) => Promise<boolean>;
  onStepChange?: (step: number | null) => void;
}

export function BrebKeyCreateWizard({
  options,
  initialStep = "intro",
  isFirstKey = true,
  onCancel,
  isRefreshing = false,
  onCreate,
  onStale,
  onNotifyHolder,
  onStepChange,
}: BrebKeyCreateWizardProps) {
  const { t } = useTranslations("payins");
  const [step, setStep] = useState<WizardStep>(initialStep);
  const [error, setError] = useState<string | null>(null);
  const [refused, setRefused] = useState(false);
  const [source, setSource] = useState<BrebKeySource | null>(
    () => options.find((option) => option.availability === "AVAILABLE")?.source ?? null,
  );

  const onStepChangeRef = useRef(onStepChange);
  useEffect(() => {
    onStepChangeRef.current = onStepChange;
  }, [onStepChange]);
  useEffect(() => {
    onStepChangeRef.current?.(STEP_INDEX[step]);
  }, [step]);
  useEffect(() => () => onStepChangeRef.current?.(null), []);

  const headingRef = useRef<HTMLHeadingElement>(null);
  const firstStep = useRef(true);
  useEffect(() => {
    if (firstStep.current) {
      firstStep.current = false;
      return;
    }
    headingRef.current?.focus();
  }, [step]);

  // The email line shows only once the holder is confirmed told; a failed notice is retried on the next refresh.
  const holderNoticesInFlight = useRef(new Set<string>());
  const [holdersTold, setHoldersTold] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => {
    if (step !== "choose" || !onNotifyHolder) return;
    for (const option of options) {
      if (!isTaken(option) || !option.taken_in_coloca) continue;
      const noticeId = noticeIdOf(option);
      if (holderNoticesInFlight.current.has(noticeId)) continue;
      holderNoticesInFlight.current.add(noticeId);
      void onNotifyHolder(option.source).then((told) => {
        if (told) setHoldersTold((current) => new Set(current).add(noticeId));
        else holderNoticesInFlight.current.delete(noticeId);
      });
    }
  }, [step, options, onNotifyHolder]);

  // A reload can take the chosen value away; the choice then falls to the first one still free.
  const selected =
    options.find((option) => option.source === source && !isTaken(option)) ??
    options.find((option) => !isTaken(option)) ??
    null;
  const allTaken = options.every(isTaken);
  const canContinue = selected?.availability === "AVAILABLE" && !isRefreshing;
  // Locked on Continue: a refresh on the confirm step must not swap the key for another one.
  const chosen = options.find((option) => option.source === source) ?? null;
  const chosenAvailable = chosen?.availability === "AVAILABLE";

  const submit = async () => {
    if (!chosen || !chosenAvailable) {
      setStep("choose");
      return;
    }
    setStep("creating");
    setError(null);
    setRefused(false);
    try {
      await onCreate(chosen.source);
    } catch (err: unknown) {
      setError(HttpApiService.getErrorMessage(err));
      setRefused(isBrebKeyCreateRefused(err));
      setStep("error");
    }
  };

  if (step === "intro") {
    return (
      <div className="space-y-5">
        <div>
          <h3 ref={headingRef} tabIndex={-1} className="text-lg font-semibold text-foreground outline-none">
            {t("breb_key_intro_title")}
          </h3>
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{t("breb_key_intro_body")}</p>
        </div>
        <BrebKeyPlate keyValue={selected?.key_value ?? KEY_PLACEHOLDER} showActions={false} />
        <ul className="space-y-2.5">
          {[
            { icon: Landmark, text: t("breb_key_intro_point_banks") },
            { icon: Wallet, text: t("breb_key_intro_point_uses") },
            { icon: QrCode, text: t("breb_key_intro_point_qr") },
          ].map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-start gap-3 text-sm text-foreground">
              <Icon className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
              <span>{text}</span>
            </li>
          ))}
        </ul>
        <div className="flex justify-end">
          <ColocaButton variant="primary" onClick={() => setStep("choose")}>
            {t("breb_key_intro_cta")}
          </ColocaButton>
        </div>
      </div>
    );
  }

  if (step === "choose") {
    return (
      <div className="space-y-5">
        <div>
          <h3 ref={headingRef} tabIndex={-1} className="text-lg font-semibold text-foreground outline-none">
            {t("breb_key_choose_title")}
          </h3>
          <p className="mt-1.5 text-sm text-muted-foreground">{t("breb_key_choose_body")}</p>
        </div>
        {allTaken && (
          <ColocaAlert
            variant="warning"
            icon={AlertCircle}
            heading={t("breb_key_all_taken_title")}
            text={t("breb_key_all_taken_body")}
          />
        )}
        <ColocaChoiceCards
          value={selected?.source ?? ""}
          onValueChange={(next) => setSource(next as BrebKeySource)}
          aria-label={t("breb_key_choose_title")}
          items={options.map((option) => {
            const Icon = BREB_KEY_SOURCE_ICON[option.source];
            return {
              value: option.source,
              disabled: isTaken(option),
              icon: <Icon className="size-5" aria-hidden="true" />,
              label: t(SOURCE_LABEL[option.source]),
              description: (
                <span className="mt-1 block space-y-1.5">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-foreground">
                      {option.key_value}
                    </span>
                    <AvailabilityBadge availability={option.availability} checking={isRefreshing} />
                  </span>
                  <span className="block text-xs text-muted-foreground">{t(SOURCE_HINT[option.source])}</span>
                  {isTaken(option) && (
                    <span className="block text-xs text-foreground">
                      {t(
                        !option.taken_in_coloca
                          ? "breb_key_taken_elsewhere"
                          : holdersTold.has(noticeIdOf(option))
                            ? "breb_key_taken_in_coloca_notified"
                            : "breb_key_taken_in_coloca",
                      )}
                    </span>
                  )}
                </span>
              ),
            };
          })}
        />

        {selected?.availability === "UNKNOWN" && !isRefreshing && (
          <ColocaAlert
            variant="warning"
            icon={AlertCircle}
            text={t("breb_key_availability_unknown_body")}
            actions={
              <ColocaButton size="sm" icon={RefreshCw} onClick={onStale}>
                {t("breb_key_availability_recheck")}
              </ColocaButton>
            }
          />
        )}

        <div className="flex justify-between gap-2">
          {initialStep === "choose" && onCancel ? (
            <ColocaButton variant="ghost" onClick={onCancel}>
              {t("breb_key_add_cancel")}
            </ColocaButton>
          ) : (
            <ColocaButton variant="ghost" icon={ChevronLeft} onClick={() => setStep("intro")}>
              {t("breb_key_back")}
            </ColocaButton>
          )}
          <ColocaButton
            variant="primary"
            disabled={!canContinue}
            onClick={() => {
              if (selected) setSource(selected.source);
              setStep("confirm");
            }}
          >
            {t("breb_key_choose_cta")}
          </ColocaButton>
        </div>
      </div>
    );
  }

  if (step === "creating") {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <Loader2 className="size-9 animate-spin text-primary motion-reduce:animate-none" aria-hidden="true" />
        <p className="text-sm text-muted-foreground" role="status">
          {t("breb_key_creating")}
        </p>
      </div>
    );
  }

  if (step === "error") {
    const backToChoose = () => {
      onStale();
      setStep("choose");
    };
    return (
      <div className="space-y-5">
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          heading={t("breb_key_error_title")}
          text={error}
        />
        <div className="flex justify-between gap-2">
          {refused ? (
            <ColocaButton variant="primary" className="ml-auto" onClick={backToChoose}>
              {t("breb_key_choose_other")}
            </ColocaButton>
          ) : (
            <>
              <ColocaButton variant="ghost" icon={ChevronLeft} onClick={backToChoose}>
                {t("breb_key_back")}
              </ColocaButton>
              <ColocaButton variant="primary" icon={RefreshCw} onClick={() => void submit()}>
                {t("breb_key_retry")}
              </ColocaButton>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <h3 ref={headingRef} tabIndex={-1} className="text-lg font-semibold text-foreground outline-none">
        {t("breb_key_confirm_title")}
      </h3>
      {chosen && <BrebKeyPlate keyValue={chosen.key_value} showActions={false} />}
      {!chosenAvailable && (
        <ColocaAlert variant="warning" icon={AlertCircle} text={t("breb_key_choice_gone")} />
      )}
      <ul className="space-y-2.5">
        {[
          t("breb_key_confirm_permanent"),
          t(isFirstKey ? "breb_key_confirm_qr" : "breb_key_confirm_qr_secondary"),
        ].map((text) => (
          <li key={text} className="flex items-start gap-3 text-sm text-foreground">
            <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
            <span>{text}</span>
          </li>
        ))}
      </ul>
      <div className="flex justify-between gap-2">
        <ColocaButton variant="ghost" icon={ChevronLeft} onClick={() => setStep("choose")}>
          {t("breb_key_back")}
        </ColocaButton>
        <ColocaButton variant="primary" disabled={!chosenAvailable} onClick={() => void submit()}>
          {t("breb_key_confirm_cta")}
        </ColocaButton>
      </div>
    </div>
  );
}

function AvailabilityBadge({
  availability,
  checking,
}: {
  availability: BrebKeyAvailability | null;
  checking: boolean;
}) {
  const { t } = useTranslations("payins");
  if (checking) {
    return (
      <ColocaBadge variant="secondary" icon={Loader2}>
        {t("breb_key_availability_checking")}
      </ColocaBadge>
    );
  }
  if (availability === "AVAILABLE") {
    return (
      <ColocaBadge variant="success" icon={CheckCircle2}>
        {t("breb_key_availability_available")}
      </ColocaBadge>
    );
  }
  if (availability === "TAKEN") {
    return <ColocaBadge variant="destructive">{t("breb_key_availability_taken")}</ColocaBadge>;
  }
  if (availability === "UNKNOWN") {
    return <ColocaBadge variant="warning">{t("breb_key_availability_unknown")}</ColocaBadge>;
  }
  return null;
}
